import React from "react";
import { LoadPhase, SkeletonCard } from "../../../components/ui";

/** The tile's own footprint, so the block that stands in for it is the size of
 *  the tile that lands in its place. */
const FOOTPRINT = "min-h-[120px] sm:min-h-[150px] 3xl:min-h-[180px]";

interface StatTileProps {
  label: string;
  value: number | string;
  /**
   * What to write in place of the figure when there is nothing to count yet —
   * "No accounts yet" rather than a 0 the size of a headline, which reads as a
   * figure that fell rather than one that has not started.
   */
  emptyMessage?: string | null;
  /**
   * Where the figure is. The tile is not drawn at all until it arrives — a
   * label over a blank space reads as a tile with nothing in it — so a wait
   * long enough to notice shimmers the whole card instead.
   */
  phase?: LoadPhase;
}

/** A single headline number — no plot, so no tooltip layer. */
const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  emptyMessage = null,
  phase = "ready",
}) =>
  phase !== "ready" ? (
    <SkeletonCard phase={phase} className={FOOTPRINT} />
  ) : (
    <section
      className={`rounded-md border border-line bg-white p-4 sm:p-5 3xl:p-7 flex flex-col justify-between gap-6 ${FOOTPRINT}`}
    >
      <p className="text-[14px] 3xl:text-[16px] text-ink-muted">{label}</p>
      {emptyMessage ? (
        <p className="text-[15px] sm:text-[16px] 3xl:text-[19px] text-ink">
          {emptyMessage}
        </p>
      ) : (
        <p className="text-[26px] sm:text-[30px] 3xl:text-[38px] font-semibold text-ink leading-none">
          {value}
        </p>
      )}
    </section>
  );

export default StatTile;
